import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from "@angular/common/http";
import { Observable } from "rxjs";
import { GenCartTypes, GenHandlingOption, GenShippingAccount } from "../models/cart.model";
import { map } from "rxjs/operators";
import { GenCheckoutInfoItem, GenCheckPaymentResponse, GenEngineReplacementForm, GenOrderDocumentList, GenOrderInstructionsNotes } from "../models/checkout.model";
import { GenPaymentTypeEnum } from "../enums/payment-type.enum";
import { Address, OccEndpointsService, WindowRef } from '@spartacus/core';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { CheckoutStepType } from '@spartacus/checkout/base/root';
import { GenCartType } from '../enums/cart-type.enum';
import { PaymentType } from '@spartacus/cart/base/root';

@Injectable({
  providedIn: 'root'
})
export class GenCheckoutService {

  constructor(
    protected winRef: WindowRef,
    private httpClient: HttpClient,
    private occEndpoints: OccEndpointsService,
    private checkoutStepService: CheckoutStepService,
  ) { }

  getSpecialHandlingOptions(cartNumber: string, userEmail: string, shippingGroupName: string): Observable<GenHandlingOption[]> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userEmail}/carts/${cartNumber}/groups/${shippingGroupName}/gethandlingoptions`;
    return this.httpClient.get(url).pipe(
      map((response: any) => response.handlingOptions
        .sort((a: GenHandlingOption, b: GenHandlingOption) => (a.code > b.code ? 1 : -1)))
    );
  }

  getCheckoutInfo(cartNumber: string, userId: string, shippingGroupName: string, manufactureGroupId: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartNumber}/groups/${shippingGroupName}/manufactures/${manufactureGroupId}/getInformation`;
    return this.httpClient.get(url);
  }

  getPaymentTypes(cartNumber: string, userId: string): Observable<PaymentType[]> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartNumber}/paymenttypes`;
    return this.httpClient.get(url).pipe(
      map((response: any) => response.paymentTypes)
    );
  }

  updateSpecialHandlingOptions(
    cartNumber: string,
    userEmail: string,
    shippingGroupName: string,
    optionCodes: string
  ): Observable<GenHandlingOption[]> {
    const params = { optionCodes };
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userEmail}/carts/${cartNumber}/groups/${shippingGroupName}/sethandlingoptions`;
    return this.httpClient.put(url, {}, { params })
      .pipe(map((response: any) => response.handlingOptions))
  }

  setCheckoutInformation(body: GenCheckoutInfoItem, userId: string, cartNumber: string, shippingGroupName: string, manufactureGroupId: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartNumber}/groups/${shippingGroupName}/manufactures/${manufactureGroupId}/setInformation`;
    return this.httpClient.post(url, body)
      .pipe(map((response: any) => response))
  }

  getFreightAddress(userId: string, cartId: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/addresses/freight`;
    return this.httpClient.get(url);
  }

  setDeliveryAddress(addressId: any, userId: string, cartId: string, payload: any): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/addresses/customAddress?dropShipAddressId=${addressId}`;
    return this.httpClient.post(url, payload);
  }

  addFFAddress(userId: string, cartId: string, payload: any): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/addresses/freight`;
    return this.httpClient.post(url, payload);
  }

  checkPaymentPossibility(userId: string, cartId: string, paymentType: GenPaymentTypeEnum): Observable<GenCheckPaymentResponse> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/paymenttype?paymentTypeCode=${paymentType}`;
    return this.httpClient.get<GenCheckPaymentResponse>(url);
  }

  uploadOrderDocuments(cartId: string, files: File[]): Observable<HttpEvent<GenOrderDocumentList>> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const url = `${this.occEndpoints.getBaseUrl()}/users/current/carts/${cartId}/documents`;
    return this.httpClient.post<GenOrderDocumentList>(url, formData, {
      observe: 'events',
      reportProgress: true,
    });
  }

  removeOrderDocument(cartId: string, documentCode: string): Observable<void> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/current/carts/${cartId}/documents/${encodeURIComponent(documentCode)}`;
    return this.httpClient.delete<void>(url);
  }

  disableCheckoutSteps(cartTypes: GenCartTypes[] | undefined, isEngineReplacement?: boolean): void {
    const isSingleSubscription = cartTypes?.length == 1 && this.hasSpecificCartTypes(cartTypes, GenCartType.SUBSCRIPTION);
    const isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));

    this.checkoutStepService.disableEnableStep(
      CheckoutStepType.DELIVERY_ADDRESS,
      isSingleSubscription
    );
    this.checkoutStepService.disableEnableStep(
      CheckoutStepType.SUBSCRIPTION_INFORMATION,
      !cartTypes?.some(ct => ct === GenCartType.SUBSCRIPTION.toString())
    );
    this.checkoutStepService.disableEnableStep(
      CheckoutStepType.SPECIAL_HANDLING,
      isSingleSubscription || isServicePartsEnabled
    );
    this.checkoutStepService.disableEnableStep(
      CheckoutStepType.FINISHED_GOODS,
      !(this.hasSpecificCartTypes(cartTypes, GenCartType.FINISHED_GOODS)) || !isServicePartsEnabled
    );
    this.checkoutStepService.disableEnableStep(
      CheckoutStepType.SERVICE_PARTS,
      !(this.hasSpecificCartTypes(cartTypes, GenCartType.SERVICE_PARTS)) || !isServicePartsEnabled
    );
    this.checkoutStepService.disableEnableStep(
      CheckoutStepType.ENGINE_REPLACEMENT_FORM,
      !isEngineReplacement
    );
  }

  hasSpecificCartTypes(cartTypes: GenCartTypes[] | undefined, cartType: GenCartType): boolean {
    return cartTypes?.some(ct => ct === cartType.toString());
  }

  getShippingGroupDisplayName(cartType: string): string {
    if (!cartType.match(/.*S$/)) {
      cartType = cartType + 'S';
    }
    return cartType.toLowerCase().replace('_', ' ');
  }

  getDropShipAddresses(userId: string): Observable<{ message?: string, addresses?: Address[] }> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/dropShipAddresses`;
    return this.httpClient.get(url).pipe(map((res: any) => {
      return {
        addresses: res.addresses as Address[],
        message: res.message,
      };
    }));
  }

  addDropShipAddress(userId: string, address: Address): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/dropShipAddress`;
    return this.httpClient.post(url, address);
  }

  editDropShipAddress(userId: string, addressId: string, address: Address): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/dropShipAddress/${addressId}`;
    return this.httpClient.put(url, address);
  }

  deleteDropShipAddress(userId: string, addressId: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/dropShipAddress/${addressId}`;
    return this.httpClient.delete(url);
  }

  getDropShipAddressById(userId: string, addressId: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/dropShipAddress/${addressId}`;
    return this.httpClient.get(url);
  }

  getOrderInstructionsNotes(userId: string, cartId: string): Observable<GenOrderInstructionsNotes> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/instructions/notes`;
    return this.httpClient.get(url);
  }

  setOrderInstructions(userId: string, cartId: string, instructions: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/instructions`;
    return this.httpClient.post(url, { instructions });
  }

  setDeliveryOptions(userId: string, cartId: string, shippingGroupName: string, carrier: GenShippingAccount, confirmOutOfStock: boolean = false): Observable<any> {
    let url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/groups/${shippingGroupName}/setdeliveryoptions`;
    if (confirmOutOfStock) url += `?confirmOutOfStock=${confirmOutOfStock}`;
    return this.httpClient.post(url, carrier);
  }

  sanitizeCartForWellsFargo(cartId: string, userId: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/wf-sanitize`;
    return this.httpClient.post(url, {});
  }

  revertSanitizedWellsFargoCart(cartId: string, userId: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/revert-wf-sanitize`;
    return this.httpClient.post(url, {});
  }

  saveSppFormValue(cartId: string, userId: string, entryNumber: string, sppFormValue: string): Observable<any> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userId}/carts/${cartId}/entries/${entryNumber}/sppForm`;
    return this.httpClient.post(url, sppFormValue);
  }

  getEngineReplacementAttestation(cartNumber: string, userEmail: string): Observable<GenEngineReplacementForm> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userEmail}/carts/${cartNumber}/engine-replacement-attestation`;
    return this.httpClient.get(url);
  }

  updateEngineReplacementAttestation(
    cartNumber: string,
    userEmail: string,
    engineReplacementForm: GenEngineReplacementForm
  ): Observable<GenEngineReplacementForm> {
    const url = `${this.occEndpoints.getBaseUrl()}/users/${userEmail}/carts/${cartNumber}/engine-replacement-attestation`;
    return this.httpClient.post(url, engineReplacementForm);
  }
}
